'use client';

import React, { useState, useEffect, useCallback } from 'react';
import {
  Search,
  Plus,
  Edit2,
  Trash2,
  Eye,
  EyeOff,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Save,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';
import Modal from '@/components/ui/Modal';
import { toast } from 'sonner';
import { createClient } from '@/lib/supabase/client';

interface FAQ {
  id: string;
  question: string;
  answer: string;
  category: string;
  is_active: boolean;
  sort_order: number;
  created_at: string;
}

const CATEGORIES = ['general', 'payments', 'disputes', 'verification', 'artisans', 'customers'];

const emptyForm: Omit<FAQ, 'id' | 'created_at'> = {
  question: '',
  answer: '',
  category: 'general',
  is_active: true,
  sort_order: 0,
};

export default function FAQsContent() {
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem] = useState<FAQ | null>(null);
  const [form, setForm] = useState<Omit<FAQ, 'id' | 'created_at'>>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteModal, setDeleteModal] = useState<{ open: boolean; id: string; question: string }>({
    open: false,
    id: '',
    question: '',
  });
  const [deleting, setDeleting] = useState(false);
  const perPage = 10;

  const fetchFAQs = useCallback(async () => {
    setLoading(true);
    const supabase = createClient();
    const { data } = await supabase
      .from('faqs')
      .select('*')
      .order('sort_order', { ascending: true });
    setFaqs(data ?? []);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchFAQs();
  }, [fetchFAQs]);

  const filtered = faqs.filter((f) => {
    const q = search.toLowerCase();
    const matchSearch =
      f.question.toLowerCase().includes(q) || f.answer.toLowerCase().includes(q);
    const matchCat = categoryFilter === 'all' || f.category === categoryFilter;
    return matchSearch && matchCat;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const paginated = filtered.slice((page - 1) * perPage, page * perPage);

  const openCreate = () => {
    setEditItem(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (f: FAQ) => {
    setEditItem(f);
    setForm({
      question: f.question,
      answer: f.answer,
      category: f.category,
      is_active: f.is_active,
      sort_order: f.sort_order,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.question.trim() || !form.answer.trim()) {
      toast.error('Question and answer are required');
      return;
    }
    setSaving(true);
    const supabase = createClient();
    if (editItem) {
      const { error } = await supabase.from('faqs').update(form).eq('id', editItem.id);
      if (error) {
        toast.error('Failed to update FAQ');
      } else {
        toast.success('FAQ updated');
        setModalOpen(false);
        await fetchFAQs();
      }
    } else {
      const { error } = await supabase.from('faqs').insert(form);
      if (error) {
        toast.error('Failed to create FAQ');
      } else {
        toast.success('FAQ created');
        setModalOpen(false);
        await fetchFAQs();
      }
    }
    setSaving(false);
  };

  const handleToggleActive = async (f: FAQ) => {
    const supabase = createClient();
    const { error } = await supabase
      .from('faqs')
      .update({ is_active: !f.is_active })
      .eq('id', f.id);
    if (error) {
      toast.error('Failed to update status');
    } else {
      toast.success(f.is_active ? 'FAQ hidden' : 'FAQ published');
      await fetchFAQs();
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    const supabase = createClient();
    const { error } = await supabase.from('faqs').delete().eq('id', deleteModal.id);
    if (error) {
      toast.error('Failed to delete FAQ');
    } else {
      toast.success('FAQ deleted');
      setDeleteModal({ open: false, id: '', question: '' });
      await fetchFAQs();
    }
    setDeleting(false);
  };

  const categoryCount = (cat: string) => faqs.filter((f) => f.category === cat).length;

  return (
    <div className="px-6 py-6 max-w-screen-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-700 text-foreground">FAQs</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Manage frequently asked questions shown on the platform
          </p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-600 hover:bg-primary/90 transition-colors"
        >
          <Plus size={16} />
          Add FAQ
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total', value: faqs.length },
          { label: 'Active', value: faqs.filter((f) => f.is_active).length },
          { label: 'Hidden', value: faqs.filter((f) => !f.is_active).length },
          { label: 'Categories', value: new Set(faqs.map((f) => f.category)).size },
        ].map((s) => (
          <div key={s.label} className="card-elevated rounded-xl px-4 py-3">
            <p className="text-xs text-muted-foreground mb-1">{s.label}</p>
            <p className="text-2xl font-700 font-tabular text-foreground">{s.value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="card-elevated rounded-xl p-4 mb-4 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search FAQs..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="w-full pl-9 pr-4 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
        </div>
        <select
          value={categoryFilter}
          onChange={(e) => { setCategoryFilter(e.target.value); setPage(1); }}
          className="px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary/20"
        >
          <option value="all">All Categories</option>
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {c.charAt(0).toUpperCase() + c.slice(1)} ({categoryCount(c)})
            </option>
          ))}
        </select>
      </div>

      {/* FAQ List */}
      <div className="card-elevated rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-border bg-muted/40 flex items-center justify-between">
          <p className="text-xs font-600 text-muted-foreground uppercase tracking-wide">
            {filtered.length} FAQs
          </p>
        </div>

        {loading ? (
          <div className="divide-y divide-border/60">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="px-4 py-4">
                <div className="h-4 bg-muted rounded animate-pulse mb-2 w-3/4" />
                <div className="h-3 bg-muted rounded animate-pulse w-1/4" />
              </div>
            ))}
          </div>
        ) : paginated.length === 0 ? (
          <div className="px-4 py-12 text-center text-muted-foreground text-sm">
            No FAQs found
          </div>
        ) : (
          <div className="divide-y divide-border/60">
            {paginated.map((f) => (
              <div key={f.id} className="px-4 py-4">
                <div className="flex items-start justify-between gap-4">
                  <button
                    onClick={() => setExpandedId(expandedId === f.id ? null : f.id)}
                    className="flex-1 text-left"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-accent/10 text-accent font-500 capitalize">
                        {f.category}
                      </span>
                      <StatusBadge status={f.is_active ? 'active' : 'inactive'} />
                      <span className="text-xs text-muted-foreground">#{f.sort_order}</span>
                    </div>
                    <p className="text-sm font-600 text-foreground">{f.question}</p>
                    {expandedId === f.id && (
                      <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{f.answer}</p>
                    )}
                  </button>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => setExpandedId(expandedId === f.id ? null : f.id)}
                      className="p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground"
                    >
                      {expandedId === f.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </button>
                    <button
                      onClick={() => handleToggleActive(f)}
                      className="p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                      title={f.is_active ? 'Hide' : 'Publish'}
                    >
                      {f.is_active ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                    <button
                      onClick={() => openEdit(f)}
                      className="p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                    >
                      <Edit2 size={14} />
                    </button>
                    <button
                      onClick={() => setDeleteModal({ open: true, id: f.id, question: f.question })}
                      className="p-1.5 rounded-lg hover:bg-red-50 transition-colors text-muted-foreground hover:text-red-600"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-muted/20">
            <p className="text-xs text-muted-foreground">
              {filtered.length} FAQs · Page {page} of {totalPages}
            </p>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-1.5 rounded-lg hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-1.5 rounded-lg hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editItem ? 'Edit FAQ' : 'Add FAQ'}
        size="md"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-600 text-muted-foreground mb-1.5">Question *</label>
            <input
              type="text"
              value={form.question}
              onChange={(e) => setForm((f) => ({ ...f, question: e.target.value }))}
              className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary/20"
              placeholder="e.g. How does payment work?"
            />
          </div>
          <div>
            <label className="block text-xs font-600 text-muted-foreground mb-1.5">Answer *</label>
            <textarea
              value={form.answer}
              onChange={(e) => setForm((f) => ({ ...f, answer: e.target.value }))}
              rows={4}
              className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none"
              placeholder="Detailed answer..."
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-600 text-muted-foreground mb-1.5">Category</label>
              <select
                value={form.category}
                onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c.charAt(0).toUpperCase() + c.slice(1)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-600 text-muted-foreground mb-1.5">Sort Order</label>
              <input
                type="number"
                min={0}
                value={form.sort_order}
                onChange={(e) => setForm((f) => ({ ...f, sort_order: Number(e.target.value) }))}
                className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="faq-active"
              checked={form.is_active}
              onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
              className="rounded"
            />
            <label htmlFor="faq-active" className="text-sm text-foreground">
              Active (visible on site)
            </label>
          </div>
          <div className="flex items-center justify-end gap-3 pt-2 border-t border-border">
            <button
              onClick={() => setModalOpen(false)}
              className="px-4 py-2 text-sm font-500 text-muted-foreground hover:text-foreground transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-600 hover:bg-primary/90 disabled:opacity-60 transition-colors"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {editItem ? 'Update' : 'Create'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirm */}
      <Modal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, id: '', question: '' })}
        title="Delete FAQ"
        size="sm"
      >
        <p className="text-sm text-muted-foreground mb-4">
          Are you sure you want to delete this FAQ? This cannot be undone.
        </p>
        <p className="text-sm font-600 text-foreground mb-4 italic">
          &ldquo;{deleteModal.question}&rdquo;
        </p>
        <div className="flex items-center justify-end gap-3">
          <button
            onClick={() => setDeleteModal({ open: false, id: '', question: '' })}
            className="px-4 py-2 text-sm font-500 text-muted-foreground hover:text-foreground transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-600 hover:bg-red-700 disabled:opacity-60 transition-colors"
          >
            {deleting && <Loader2 size={14} className="animate-spin" />}
            Delete
          </button>
        </div>
      </Modal>
    </div>
  );
}
